import classNames from 'classnames/bind';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';

import Container from 'components/Auth/Shared/Container';
import ForceSSO from 'components/Auth/Shared/ForceSSO';
import Social from 'components/Auth/Shared/Social';
import SSO from 'components/Auth/Shared/SSO';
import Button from 'uikit/Button';
import InputText from 'uikit/InputText';

import ELEPHANT_IMAGE from './images/elephant.svg';
import styles from './Login.module.scss';

const cx = classNames.bind(styles);

const Login = ({ defaultUsername, handleLogin, t, validationSchema }) => (
  <Container
    action={
      <Button as={Link} data-testid="register-link" to="/auth/register">
        {t('Auth/Login/register')}
      </Button>
    }
    sideImage={ELEPHANT_IMAGE}
  >
    <Formik
      initialValues={{
        username: defaultUsername,
        password: '',
        organization: null,
      }}
      onSubmit={handleLogin}
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, status, values }) => (
        <Form className={cx('container')}>
          <div className={cx('section')}>
            <h1 className={cx('title')}>{t('Auth/Login/title')}</h1>
            <div className={cx('subtitle-container')}>
              <p className={cx('subtitle')}>{t('Auth/Login/subtitle')}</p>
              <Link
                to={{
                  pathname: '/auth/register',
                  state: {
                    email: values.username,
                  },
                }}
              >
                <p className={cx('link')}>{t('Auth/register')}</p>
              </Link>
            </div>
            {status === '404' ? (
              <p className={cx('error')} role="alert">
                <Trans i18nKey="Auth/Login/login-error-username-not-found">
                  Auth/Login/login-error-username-not-found
                  <Link
                    to={{
                      pathname: '/auth/register',
                      state: {
                        username: values.username,
                      },
                    }}
                  >
                    Auth/Login/login-error-username-not-found
                  </Link>
                  Auth/Login/login-error-username-not-found
                </Trans>
              </p>
            ) : (
              status && (
                <p className={cx('error')} role="alert">
                  {t('Auth/Login/login-error')}
                </p>
              )
            )}
            <InputText
              className={cx('field')}
              data-testid="username"
              icon="at"
              name="username"
              placeholder={t('Auth/Login/username-label')}
            />
            {(!values.organization ||
              (values.organization && !values.organization.forceSSO)) && (
              <>
                <InputText
                  icon="lock"
                  data-testid="password"
                  name="password"
                  type="password"
                  placeholder={t('Auth/Login/password-label')}
                />
                <Link
                  to={{
                    pathname: '/auth/forgot-password',
                    state: {
                      email: values.username,
                    },
                  }}
                >
                  <p className={cx('helper', 'forgot-password')}>
                    {t('Auth/Login/forgot-password')}
                  </p>
                </Link>
                {!!values.username && !!values.password && (
                  <Button
                    className={cx('action')}
                    id="login-button"
                    intent="primary"
                    isLoading={isSubmitting}
                    type="submit"
                    size="large"
                  >
                    {t('Auth/Login/login')}
                  </Button>
                )}
              </>
            )}
            <Social />
          </div>
          <SSO />
          <ForceSSO />
        </Form>
      )}
    </Formik>
  </Container>
);

Login.propTypes = {
  defaultUsername: PropTypes.string.isRequired,
  handleLogin: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  validationSchema: PropTypes.object.isRequired,
};

export default Login;
